import React from "react";
import { DataState } from "../core/hooks/useData";
import { EntityType, RefTable } from "../types";
import { useChannel } from "../core/hooks/useChannel";
import { InspectorState } from "./Inspector";
import { ReportStructure } from "../core/pdf/types";
import { NamedScopeObject, ScriptEngine } from "../core/utils/script";

export type ElementType =
	"checkbox" |
	"text" |
	"heading" |
	"radio" |
	"list" |
	"dropdown" |
	"dynamic-dropdown" |
	"info" | // i.e. a bit of text that isn't editable
	"sub-form" | 
	"container" |
	"date" |
	"calc" |
	"legal-element" |
	"aus-address-search" |
	"resource-picker" |
	"acn" |
	"entity-lookup" |
	"report" | 
	"succession-plan" |
	"group" |
	"checkbox-list" |
	"icon-choice" |
	"chart" |
	"button" |
	"gateway-form" |
	"script"
	;

export type ActionType = 
	"visibility"
	| "script"
	;

export interface Action {
	type: ActionType,
	options?: any,
}

export type EventType = 
	"change" | 
	"before-save" |
	"validate" |
	"default-value" |
	"delete-item"
	;

export interface EventContext {
	type: EventType,
	def: Element,
	//data: DataState<any>,
	container: ContainerState,
	presenter: PresenterState,

	debugContext: string[],

	//scope?: any,
	dataWindows?: NamedScopeObject[],

	options?: any,
}

export type ValidationType = 
	"required" |
	"char-limit" |
	"data-type" |
	"range" | 
	"age" | 
	"occurrence-range" |
	"custom"
;

export type FormFeatureType = 
	"entity-search"
;

export function getFormFeatureTypeName(type: FormFeatureType) {
	const map = {
		"entity-search": "Entity Search",
	};
	return map[type];
}


export interface Element {
	type: ElementType, 

	id: string, // for ref in code, default to e.g. textbox-12 (using the nextID++ value)
	uniqueID?: string,
	
	title?: string,
	subTitle?: string,

	titleType?: "placeholder", // use placeholder instead of title

	defaultValue?: string,
	defaultValueScript?: string,

	comments?: string,

	required?: boolean,

	help?: string, // id of ref help item

	features?: {
		type: FormFeatureType,
		options?: any,
	}[],

	validations?: {
		type?: ValidationType,
		options?: any,
	}[],

	events?: {
		[key in EventType]?: Action[]
	},
}

export interface TextElement extends Element {
	multiline?: boolean,
	wide?: boolean,
	size?: number,
	typeIndicator?: string,	
}

export interface ScriptElement extends Element {
	script?: string,
	lang?: "json" | "javascript",
}

export interface ACNElement extends TextElement {

}

export interface ChartElement extends Element {

}

export interface ButtonElement extends Element {
	onClick?: string, // js for click event
}

export interface InfoElement extends Element {
	html: string,
	className?: string,
}

export interface DateElement extends Element {
}

export interface RefTableElement extends Element {
	ref: string; // id of ref_table
	valueCol: string,
	textCol: string,
	subtitleCol?: string,
	filterCol?: string,
	filterScript?: string,
	sortCol?: string,
	includeBlank?: boolean,
}

export interface RadioElement extends RefTableElement {
	layout?: "vertical",
}

export interface ListElement extends RefTableElement {
	multiSelect?: boolean,
}

export interface DropdownElement extends RefTableElement {
	multiSelect?: boolean,
}
export interface DynamicDropdownElement extends RefTableElement {
	getOptionsScript: string,
	multiSelect?: boolean,
	oneSelectionAtTime?: boolean,
}
export interface IconChoiceElement extends RefTableElement {
	iconCol?: string,
}

export interface EntityLookupElement extends Element {
	requiredTypes: EntityType[],

	addressProp?: string,
	companyProp?: string,
	acnProp?: string,
}

export interface HelpElement extends Element {
}

export interface LegalElement extends Element {
}

export interface SuccessionPlanElement extends Element {
}

export interface HeadingElement extends Element {
	outline: boolean,
}
export interface TextElement extends Element {
	quickChoices: string, // comma sep list of options
}
export interface ACNElement extends TextElement {
	
}

export interface ContainerElement extends Element {
	elements: AllElementTypes[],
	
	hideTitle?: boolean,

	repeat?: {
		itemName: string, // e.g. name of what to call each repeating item, such as "Trustee"
		overviewTitleProp?: string,
	}	

	summaryReport?: string; // id of a report to use as the summary table
}

export interface SubFormElement extends Element {
	form: string, // id of form
	bg: boolean,

	options?: string, // javascript
}

export interface ReportElement extends Element {
	report: string, // id of report
	isStatic?: boolean,

	options?: string, // javascript
}

export interface SuccessionPlanElement extends Element {
}

export interface GroupElement extends Element {
	elements: AllElementTypes[],

	layout: string,
}

export interface CheckboxListElement extends Element {
	getOptionsScript: string,
}

export interface CalculatedElement extends Element {
	calcOnSaveOnly?: boolean,
	script?: string,
	displayStyle?: "hidden" | "text" | "heading",
}

export interface EntityElement extends Element {
	validate: {
		name?: boolean,
		address?: boolean,
		dob?: boolean,
		pob?: boolean,
		acn?: boolean,
		directors?: boolean,
		trustees?: boolean,
	}
}

export interface AddressElement extends Element {
	validate: {
		noPOBox?: boolean,
		noCareOf?: boolean,
		allowInternational?: boolean,
	}
}

export type AllElementTypes = Element | TextElement | ContainerElement;

// export interface FormDef {
// 	elements: AllElementTypes[],

// 	nextID: number, // ever increasing id instead of uuid string
// }

export const PresenterMessages = {
	READY: "ready",

	BEFORE_SAVE: "before-save",

	GET_STATE: "get-state",

	STRUCTURE_CHANGE: "structure-change",
	SCROLL: "scroll",

	SAVE: "save",

	SET_VALUE: "set-value", // a way to broadcast to all fields an incoming value without having to understand/navigate structure
	GET_REPEATING_CONTAINER: "get-repeating-container",
	GET_FIRST_FIELD: "get-first-field",

	RECALC_NEEDED: "recalc-needed", // need to run logic for calculated fields
	RECALC: "recalc", // all listening calc fields to run logic now
}

export interface PresenterComponentProps {
	def: Element,
}

export interface SubTitle {
	title: string,
	sourceData: DataState<any>,
	prop: string,
	element: any,
}
export interface StructureHeading {
	title: string,
	element?: any,
	subTitles?: SubTitle[],

	repeatingFieldChannelID?: string,
}

export interface PresenterState {

	channelID: string,

	title: string,

	designMode?: boolean,

	readOnly?: boolean,

	inspectorContext: InspectorState,

	structureHeadings: StructureHeading[],

	options?: any,

	defaults: any,

	dataStates: {
		[key: string]: {
			sub: any,
			data: DataState<any>,
		}
	},

	root: DataState<any>,
	rootContainer: ContainerState,

	withinEvent: {
		type?: EventType,
		queuedEvents: EventContext[],
	},

	scriptEngine: ScriptEngine,

	setTitle: (title: string) => void,
	setGeneratedData: (name: string, data: any) => void,


	registerDataState: (data: DataState<any>) => void,
	unregisterDataState: (data: DataState<any>) => void,

	// registerField: (def: AllElementTypes) => void,
	// unregisterField: (def: AllElementTypes) => void,

	validateAll: () => boolean,

	getRefData: (id: string) => RefTable,
	getSubForm: (id: string) => ContainerElement,
	getReport: (id: string) => ReportStructure,

	getValue: (
		prop: string, 
		options: {
			dataState: DataState<any>,
			scope?: any,
			returnResultContext?: boolean,
		},
	) => any,
	setValue: (prop: string, value: any, startContext?: DataState<any>) => void,
	setFirstValue: (prop: string, value: any) => void,
	findValue: (prop: string) => any,

	setChildValue: (prop: string, value: any, startContainer: ContainerState) => void,

	triggerEvent: (context: EventContext) => void,
	runAction: (context: EventContext, action: Action) => void,

	recalc: (prop: string) => void,
}

export const PresenterContext = React.createContext<PresenterState>({} as PresenterState);

export function usePresenter() {
	const context = React.useContext(PresenterContext);
	return context;
}

export function usePresenterEvents(presenter: PresenterState, handler: (msg: any) => void) {
	useChannel(presenter.channelID, handler);
}

export interface FieldRef {
	field: AllElementTypes,
	container: ContainerState,
	channelID: string,
}

/*

- how handle the structure?
- container needs to maintain when its in scope of an already identified structure heading
- can have multiple headings in single container
- repeating container has to go under the last item in the context
*/


export interface ContainerState {

	parentContainer?: ContainerState,

	def: ContainerElement,

	data: DataState<any>,

	propPath: string,

	fields: {
		[k: string]: FieldRef,
	},

	options?: any,

	childContainers: ContainerState[],

	getChildContainer: (id: string) => ContainerState,

	registerField: (props: { field: AllElementTypes, channelID: string }) => void,
	unregisterField: (def: AllElementTypes) => void,

	registerContainer: (cs: ContainerState) => void,
	unregisterContainer: (cs: ContainerState) => void,

	getField: (id: string) => FieldRef,
	find: (id: string) => FieldRef,

	setVisibility: (id: string, visible: boolean) => void,

	refresh: () => void,
}

export const ContainerContext = React.createContext<ContainerState>({} as ContainerState);

export function useContainer() {
	const context = React.useContext(ContainerContext);
	return context;
}

export const ContainerMessages = {
	GET_REF: "get-ref",
}

export const FieldMessages = {
	SET_VISIBILITY: "set-visibility",

	GET_VISIBILITY: "get-visibility",

	GET_DEF: "get-def",
	GET_CONTAINER_DEF: "get-container-def",

	GET_CONTAINER: "get-container",

	REFRESH_PARENT_CONTAINER: "refresh-parent-container",

}
